import { useEffect, useState } from "react";
import { getPdaMenuList } from "src/apis/menuApi";
import { MenuDto } from "src/dtos/menu/menuDto";
import SpinnerOverlay from "src/components/common/SpinnerOverlay";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { getMenuComponent } from "src/utils/menu/menuUtil";
import usePDAMenuStore from "src/store/pda/usePDAMenuStore";

// 노트 아이콘 컴포넌트
const NoteIcon = ({ size = 32, color = "#ffffff" }: { size?: number; color?: string }) => (
	<svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
		<rect x="4" y="3" width="16" height="18" rx="2" stroke={color} strokeWidth="2" fill="none"/>
		<line x1="7" y1="8" x2="17" y2="8" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
		<line x1="7" y1="12" x2="17" y2="12" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
		<line x1="7" y1="16" x2="13" y2="16" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
	</svg>
);

const MenuContainer = () => {
	const [menuList, setMenuList] = useState<MenuDto[]>([]);
	const [allMenus, setAllMenus] = useState<MenuDto[]>([]); // 전체 메뉴 데이터 저장
	const [currentParentMenu, setCurrentParentMenu] = useState<MenuDto | null>(null); // 현재 부모 메뉴 (3차 메뉴 표시 시)
	const { selectedMenu, setSelectedMenu, clearSelectedMenu } = usePDAMenuStore(); // 전역 상태 사용
	const [loading, setLoading] = useState(true);
	const [iconSize, setIconSize] = useState<number>(64); // 아이콘 크기 (반응형)

	// 화면 크기에 따라 아이콘 크기 조정
	useEffect(() => {
		const updateIconSize = () => {
			const width = window.innerWidth;
			if (width < 640) {
				setIconSize(48);
			} else if (width < 768) {
				setIconSize(56);
			} else {
				setIconSize(64);
			}
		};

		updateIconSize();
		window.addEventListener('resize', updateIconSize);
		return () => window.removeEventListener('resize', updateIconSize);
	}, []);

	useEffect(() => {
		const fetchMenus = async () => {
			try {
				setLoading(true);
				const response = await getPdaMenuList();
				const flatMenus = response?.data?.data || [];
				
				if (flatMenus.length === 0) {
					setMenuList([]);
					setAllMenus([]);
					return;
				}
				
				// 전체 메뉴 데이터 저장
				setAllMenus(flatMenus);
				
				// 전체 메뉴 표시 (필터링 없이 모든 메뉴 조회)
				// useYn이 'Y'인 메뉴만 필터링
				const activeMenus = flatMenus.filter(menu => menu.useYn === 'Y');
				
				// menuSeq 기준으로 정렬
				activeMenus.sort((a, b) => a.menuSeq - b.menuSeq);
				
				setMenuList(activeMenus);
				
				// 1차 메뉴부터 표시 (PDA에서는 1차 메뉴를 그리드로 표시)
				// 평면 리스트에서 menuLevel이 1이고 useYn이 'Y'인 메뉴만 필터링
				// const firstLevelMenus = flatMenus.filter(menu => 
				// 	menu.menuLevel === 1 && menu.useYn === 'Y'
				// );
				
				// menuSeq 기준으로 정렬
				// firstLevelMenus.sort((a, b) => a.menuSeq - b.menuSeq);
				
				// setMenuList(firstLevelMenus);
			} catch (error) {
				// 에러 처리 (콘솔 로그 제거)
			} finally {
				setLoading(false);
			}
		};

		fetchMenus();
	}, []);

	const handleMenuClick = (menu: MenuDto) => {
		// 방법 1: subMenu 필드 확인 (계층 구조가 이미 있는 경우)
		if (menu.subMenu && menu.subMenu.length > 0) {
			const activeSubMenus = menu.subMenu.filter(m => m.useYn === 'Y');
			if (activeSubMenus.length > 0) {
				activeSubMenus.sort((a, b) => a.menuSeq - b.menuSeq);
				setMenuList(activeSubMenus);
				setCurrentParentMenu(menu);
				return;
			}
		}
		
		// 방법 2: 평면 리스트에서 하위 메뉴 찾기
		const subMenus = allMenus.filter(m => 
			m.upMenu === menu.menuCd && m.menuLevel === (menu.menuLevel + 1) && m.useYn === 'Y'
		);
		
		if (subMenus.length > 0) {
			// 하위 메뉴가 있으면 하위 메뉴 표시
			subMenus.sort((a, b) => a.menuSeq - b.menuSeq);
			setMenuList(subMenus);
			setCurrentParentMenu(menu);
		} else {
			// 하위 메뉴가 없으면 해당 기능 화면 표시
			setSelectedMenu(menu);
		}
	};

	const handleBackClick = () => {
		if (!currentParentMenu) return;
		
		// 현재 부모 메뉴의 레벨에 따라 상위 메뉴로 이동
		if (currentParentMenu.menuLevel === 2) {
			// 2차 메뉴 화면에서 뒤로가기 → 1차 메뉴로
			const firstLevelMenus = allMenus.filter(menu => 
				menu.menuLevel === 1 && menu.useYn === 'Y'
			);
			firstLevelMenus.sort((a, b) => a.menuSeq - b.menuSeq);
			setMenuList(firstLevelMenus);
			setCurrentParentMenu(null);
		} else if (currentParentMenu.menuLevel === 3) {
			// 3차 메뉴 화면에서 뒤로가기 → 2차 메뉴로
			// currentParentMenu는 2차 메뉴이므로, 그 2차 메뉴의 상위인 1차 메뉴를 찾아서
			// 그 1차 메뉴의 2차 메뉴들을 표시
			const firstLevelMenu = allMenus.find(m => m.menuCd === currentParentMenu.upMenu);
			if (firstLevelMenu) {
				const secondLevelMenus = allMenus.filter(menu => 
					menu.upMenu === firstLevelMenu.menuCd && menu.menuLevel === 2 && menu.useYn === 'Y'
				);
				secondLevelMenus.sort((a, b) => a.menuSeq - b.menuSeq);
				setMenuList(secondLevelMenus);
				setCurrentParentMenu(firstLevelMenu);
			}
		} else {
			// 1차 메뉴의 상위는 없으므로 1차 메뉴로
			const firstLevelMenus = allMenus.filter(menu => 
				menu.menuLevel === 1 && menu.useYn === 'Y'
			);
			firstLevelMenus.sort((a, b) => a.menuSeq - b.menuSeq);
			setMenuList(firstLevelMenus);
			setCurrentParentMenu(null);
		}
	};

	// 기능 화면에서 메뉴로 돌아가기
	const handleBackToMenu = () => {
		clearSelectedMenu();
	};

	if (loading) {
		return <SpinnerOverlay text="메뉴를 불러오는 중..." />;
	}

	// 선택된 메뉴가 있으면 해당 화면 렌더링
	if (selectedMenu) {
		const component = getMenuComponent(selectedMenu, handleBackToMenu);
		if (component) {
			return component;
		}
	}

	return (
		<div className="w-full h-full flex flex-col bg-gray-100">
			{/* 뒤로가기 버튼 (하위 메뉴 표시 시) */}
			{currentParentMenu && (
				<div className="w-full bg-white border-b border-gray-200 px-4 py-2">
					<button
						onClick={handleBackClick}
						className="flex items-center gap-2 text-gray-700 hover:text-blue-600 transition-colors"
					>
						<ArrowLeftOutlined />
						<span className="font-medium">{currentParentMenu.menuNm}</span>
					</button>
				</div>
			)}
			
			{/* 메뉴 그리드 */}
			<div className="flex-1 overflow-y-auto overflow-x-hidden p-[clamp(0.25rem,1vw,1rem)]">
				<div className="grid grid-cols-2 gap-[clamp(0.25rem,1vw,1rem)] w-full">
					{menuList.map((menu) => {
						// 해당 메뉴에 하위 메뉴가 있는지 확인
						const hasSubMenus = 
							(menu.subMenu && menu.subMenu.length > 0) ||
							allMenus.some(m => 
								m.upMenu === menu.menuCd && m.menuLevel === (menu.menuLevel + 1) && m.useYn === 'Y'
							);
						
						return (
							<button
								key={menu.menuCd}
								onClick={() => handleMenuClick(menu)}
								className="relative flex flex-col items-center justify-center bg-blue-600 hover:bg-blue-700 active:bg-blue-800 rounded-lg p-[clamp(0.5rem,1.5vw,1rem)] aspect-[3/1] min-h-[88px] w-full transition-colors duration-200 shadow-md"
							>
								{/* 하위 메뉴 표시 아이콘 */}
								{hasSubMenus && (
									<div className="absolute top-[clamp(0.275rem,1.1vw,0.55rem)] right-[clamp(0.275rem,1.1vw,0.55rem)] bg-white bg-opacity-30 rounded-full w-[clamp(1.1rem,4.4vw,1.65rem)] h-[clamp(1.1rem,4.4vw,1.65rem)] flex items-center justify-center">
										<span className="text-white text-[clamp(0.55rem,2.2vw,0.825rem)]">▶</span>
									</div>
								)}
								
								{/* 아이콘 */}
								<div className="mb-[clamp(0.275rem,1.1vw,0.825rem)] text-white flex items-center justify-center">
									{menu.menuCd && ['$$$', 'MST', 'ACC', 'TQC', 'HRM', 'SAL', 'SYS', 'EQM', 'PRD', 'WMS', 'PUR'].includes(menu.menuCd) ? (
										<img 
											src={`${process.env.PUBLIC_URL}/MESICON/${menu.menuCd}.png`} 
											alt={menu.menuCd.toLowerCase()} 
											className="w-[clamp(1.5rem,6vw,3rem)] h-[clamp(1.5rem,6vw,3rem)] object-contain" 
										/>
									) : (
										<NoteIcon size={26} color="#ffffff" />
									)}
								</div>
								
								{/* 메뉴명 */}
								<span className="text-white text-[clamp(1rem,2.75vw,1.1rem)] font-medium text-center whitespace-nowrap overflow-hidden text-ellipsis max-w-full px-1">
									{menu.menuNm}
								</span>
							</button>
						);
					})}
				</div>
			</div>
			
			{/* 푸터 */}
			{/* <div className="w-full bg-white py-3 px-4 border-t border-gray-200">
				<p className="text-gray-500 text-sm">© 2017 ITSCO.</p>
			</div> */}
		</div>
	);
};

export default MenuContainer;

